import React from 'react';

import { Box, Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import type { RoomRateRatePlanOption } from '../RoomRatesStep/RoomRateRoomRow';

import type { PackageRatePlanSource } from './ratePlanMapping';

const RATE_PLAN_SOURCE_CHIPS: Partial<Record<PackageRatePlanSource, { color?: 'info'; label: string }>> = {
  ai: { color: 'info', label: 'AI MATCH' },
  manual: { label: 'MANUAL' },
};

export interface PackageRatePlanRow {
  options: Array<RoomRateRatePlanOption>;
  packageId: string;
  packageName: string;
  roomName?: string;
  source?: PackageRatePlanSource;
}

interface Props {
  disabled?: boolean;
  onSelectRatePlan: (packageId: string, ratePlanId: string) => void;
  rows: Array<PackageRatePlanRow>;
  selectedRatePlanIdByPackageId: Record<string, string>;
}

export default function PackageRatePlanPicker({
  disabled,
  onSelectRatePlan,
  rows,
  selectedRatePlanIdByPackageId,
}: Props) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Rate plan for each package
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        These packages are in a room with more than one rate plan. We match each package to a rate plan from its name;
        choose the rate plan for any we couldn't match, and check the rest.
      </Typography>

      <Stack spacing={1.5} mt={3}>
        {rows.map((row) => {
          const selectId = `package-rate-plan-${row.packageId}`;
          const selectedRatePlanId = selectedRatePlanIdByPackageId[row.packageId] ?? '';
          const chip = row.source && RATE_PLAN_SOURCE_CHIPS[row.source];

          return (
            <Stack
              key={row.packageId}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'flex-start' }}
            >
              <Stack sx={{ justifyContent: 'center', minHeight: { sm: 40 }, minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2">{row.packageName}</Typography>
                {row.roomName && (
                  <Typography variant="caption" color="text.secondary">
                    Room: {row.roomName}
                  </Typography>
                )}
              </Stack>

              {/* Fixed width, right-aligned, so the chips line up beside the selects whichever row has one. */}
              <Box
                sx={{
                  alignItems: 'center',
                  display: { xs: chip ? 'flex' : 'none', sm: 'flex' },
                  flexShrink: 0,
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  minHeight: { sm: 40 },
                  width: { sm: 120 },
                }}
              >
                {chip && <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />}
              </Box>

              <FormControl
                size="small"
                required
                error={!selectedRatePlanId}
                sx={{ width: { xs: '100%', sm: 320 }, flexShrink: 0 }}
              >
                <InputLabel id={selectId}>Rate plan</InputLabel>
                <Select
                  label="Rate plan"
                  labelId={selectId}
                  value={selectedRatePlanId}
                  disabled={disabled}
                  onChange={(event) => onSelectRatePlan(row.packageId, event.target.value)}
                >
                  {row.options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {!selectedRatePlanId && <FormHelperText>Choose a rate plan</FormHelperText>}
              </FormControl>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
